import { Container, Row, Col, Form } from 'react-bootstrap';
import Common from '../../../shared/Common';
import { averageBy, sumBy } from './chartUtils';
import { expensesColor, incomeColor, balanceColor, taxColor, gradientStopBalanceColor, scatterColor } from './colors';
import { IGenericFinancialValue } from './types';
import { Dispatch, SetStateAction } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  ReferenceLine,
  Line,
  Legend,
  Tooltip,
} from 'recharts';
import FinancialTooltip from './FinancialTooltip';

interface FinancialChartProps {
  data: IGenericFinancialValue[];
  isWeekly: boolean;
  showReferenceLines: boolean | undefined;
  showExpenses: boolean | undefined;
  showIncomes: boolean | undefined;
  showBalance: boolean | undefined;
  showTaxes: boolean | undefined;
  includeTaxes: boolean | undefined;
  showSums: boolean | undefined;
  groupByYears: boolean | undefined;
  balanceTarget: number;
  chartHeight: number;
  setShowExpenses: Dispatch<SetStateAction<boolean | undefined>>;
  setShowIncomes: Dispatch<SetStateAction<boolean | undefined>>;
  setShowBalance: Dispatch<SetStateAction<boolean | undefined>>;
  setShowTaxes: Dispatch<SetStateAction<boolean | undefined>>;
}

const FinancialChart = ({
  data,
  isWeekly,
  showReferenceLines,
  showExpenses,
  showIncomes,
  showBalance,
  showTaxes,
  includeTaxes,
  showSums,
  groupByYears,
  balanceTarget,
  chartHeight,
  setShowExpenses,
  setShowIncomes,
  setShowBalance,
  setShowTaxes,
}: FinancialChartProps) => {
  const avgExpense = averageBy(data, (val) => val.totalExpense);
  const avgIncome = averageBy(data, (val) => val.totalIncome);
  const avgBalance = averageBy(data, (val) => val.balance);
  const sumExpense = sumBy(data, (val) => val.totalExpense);
  const sumIncome = sumBy(data, (val) => val.totalIncome);
  const sumBalance = sumBy(data, (val) => val.balance);
  const sumTax = sumBy(data, (val) => val.totalTax);

  const RenderLegend = () => {
    const type = isWeekly ? 'weekly' : 'monthly';
    return (
      <Container>
        <Row xs='auto' className='justify-content-center align-items-center'>
          <Col>
            <Form.Check
              id={`show-expenses-${type}-switch`}
              label={
                <div style={{ color: expensesColor }}>
                  Wydatki&nbsp;
                  {showSums && showExpenses && <span>({Common.Utils.getCurrencyString(sumExpense)})</span>}
                </div>
              }
              style={{ userSelect: 'none' }}
              checked={showExpenses}
              onChange={() => {
                setShowExpenses(!showExpenses);
              }}
            />
          </Col>
          <Col>
            <Form.Check
              id={`show-incomes-${type}-switch`}
              label={
                <div style={{ color: incomeColor }}>
                  {includeTaxes ? 'Przychód' : 'Dochód'}&nbsp;
                  {showSums && showIncomes && <span>({Common.Utils.getCurrencyString(sumIncome)})</span>}
                </div>
              }
              style={{ userSelect: 'none' }}
              checked={showIncomes}
              onChange={() => {
                setShowIncomes(!showIncomes);
              }}
            />
          </Col>
          {!includeTaxes && (
            <Col>
              <Form.Check
                id={`show-taxes-${type}-switch`}
                label={
                  <div style={{ color: taxColor }}>
                    Podatki&nbsp;
                    {showSums && showTaxes && <span>({Common.Utils.getCurrencyString(sumTax)})</span>}
                  </div>
                }
                style={{ userSelect: 'none' }}
                checked={showTaxes}
                onChange={() => {
                  setShowTaxes(!showTaxes);
                }}
              />
            </Col>
          )}
          <Col>
            <Form.Check
              id={`show-balance-${type}-switch`}
              label={
                <div style={{ color: balanceColor }}>
                  Bilans&nbsp;
                  {showSums && showBalance && <span>({Common.Utils.getCurrencyString(sumBalance)})</span>}
                </div>
              }
              style={{ userSelect: 'none' }}
              checked={showBalance}
              onChange={() => {
                setShowBalance(!showBalance);
              }}
            />
          </Col>
        </Row>
      </Container>
    );
  };

  const gradientOffset = () => {
    const threshold = isWeekly ? 0 : balanceTarget;
    const dataMax = Math.max(...data.map((i) => i.balance)) - threshold;
    const dataMin = Math.min(...data.map((i) => i.balance)) - threshold;

    if (dataMax <= 0) {
      return 0;
    }
    if (dataMin >= 0) {
      return 1;
    }

    return dataMax / (dataMax - dataMin);
  };

  const off = gradientOffset();

  return (
    <div className='overflow-auto'>
      <ResponsiveContainer minWidth={600} width='100%' height={chartHeight}>
        <ComposedChart
          accessibilityLayer={false}
          data={data}
          margin={{
            left: 10,
            right: 25,
            top: 5,
            bottom: 5,
          }}
        >
          <Scatter key={0} name={'Wydatki'} line={{ strokeWidth: 0.75 }} fill={scatterColor} cursor='pointer' />
          <CartesianGrid strokeDasharray='1' />
          {/* YYYYMMWW - nie mamy normalnej daty, więc trzeba generowac liczbę będącą reprezentacją tygodni i miesięcy */}
          <XAxis
            dataKey={(data: IGenericFinancialValue) => data.year * 10000 + (data.month ?? 0) * 100 + (data.week ?? 0)}
            tickFormatter={(value) => (isWeekly ? (value % 100).toString() : ((value / 100) % 100).toString())}
            name={isWeekly ? 'Tydzień' : 'Miesiąc'}
          />
          <YAxis width={'auto'} unit=' zł' />
          {showBalance && (
            <>
              <defs>
                <linearGradient id='splitColorLowOpacity' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset={off} stopColor={balanceColor} stopOpacity={0.1} />
                  <stop offset={off} stopColor={gradientStopBalanceColor} stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id='splitColor' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset={off} stopColor={balanceColor} stopOpacity={1} />
                  <stop offset={off} stopColor={gradientStopBalanceColor} stopOpacity={1} />
                </linearGradient>
              </defs>
              <Area
                type='monotone'
                dataKey='balance'
                stroke={isWeekly ? balanceColor : 'url(#splitColor)'}
                fill={isWeekly ? balanceColor : 'url(#splitColorLowOpacity)'}
                name='Bilans'
                unit=' zł'
                activeDot={{ r: 6 }}
                baseValue={isWeekly ? 0 : balanceTarget}
              />
              <ReferenceLine
                y={isWeekly ? 0 : balanceTarget}
                strokeWidth={0.7}
                stroke={balanceColor}
                label={
                  !isWeekly && {
                    position: 'right',
                    value: 'cel',
                    style: { fontSize: '14px', fill: balanceColor },
                  }
                }
              />
            </>
          )}
          {showExpenses && (
            <Line
              type='monotone'
              dataKey='totalExpense'
              stroke={expensesColor}
              name='Wydatki'
              unit=' zł'
              activeDot={{ r: 6 }}
            />
          )}
          {showIncomes && (
            <Line
              type='monotone'
              dataKey='totalIncome'
              stroke={incomeColor}
              name={includeTaxes ? 'Przychód' : 'Dochód'}
              unit=' zł'
              activeDot={{ r: 6 }}
            />
          )}
          {showTaxes && (
            <Line type='monotone' dataKey='totalTax' stroke={taxColor} name='Podatki' unit=' zł' activeDot={{ r: 6 }} />
          )}
          <Legend content={<RenderLegend />} verticalAlign='top' iconSize={10} height={36} />
          {showReferenceLines && (
            <>
              {showExpenses && (
                <ReferenceLine
                  y={avgExpense}
                  label={{
                    dx: 10,
                    dy: -10,
                    value: `średnia: ${avgExpense.toFixed(2)} zł`,
                    style: { fontSize: '14px', fontWeight: 'bold', fill: expensesColor },
                  }}
                  stroke={expensesColor}
                  strokeDasharray='3 3'
                />
              )}
              {showIncomes && (
                <ReferenceLine
                  y={avgIncome}
                  label={{
                    dx: -200,
                    dy: -10,
                    value: `średnia: ${avgIncome.toFixed(2)} zł`,
                    style: { fontSize: '14px', fontWeight: 'bold', fill: incomeColor },
                  }}
                  stroke={incomeColor}
                  strokeDasharray='3 3'
                />
              )}
              {showBalance && (
                <ReferenceLine
                  y={avgBalance}
                  label={{
                    dx: 200,
                    dy: -10,
                    value: `średnia: ${avgBalance.toFixed(2)} zł`,
                    style: { fontSize: '14px', fontWeight: 'bold', fill: balanceColor },
                  }}
                  stroke={balanceColor}
                  strokeDasharray='3 3'
                />
              )}
            </>
          )}
          <Tooltip
            content={(props) => (
              <FinancialTooltip
                {...props}
                isWeekly={isWeekly}
                groupByYears={groupByYears ?? false}
                expensesColor={expensesColor}
                incomeColor={incomeColor}
                includeTaxes={includeTaxes}
                balanceColor={balanceColor}
                taxColor={taxColor}
              />
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FinancialChart;
